/**
 * O interpretador do fluxo — o "cérebro" compartilhado.
 *
 * É puro de propósito: não toca em DOM, não faz `fetch`, não executa código. Ele recebe a
 * config e o estado, e devolve uma lista de **efeitos** que o host (SDK no site do visitante
 * ou preview do painel) executa. Só assim o mesmo interpretador roda nos dois lugares sem
 * divergir — que é a decisão central do planejamento.
 */
import { evaluateCondition, ExpressionError } from './expression.js';
import { interpolate, interpolateDeep, stringifyValue } from './interpolate.js';
import { DEFAULT_TIMEOUT_MS, MAX_TIMEOUT_MS } from './schema.js';
/** Handle de saída padrão dos blocos lineares. Arestas sem handle também contam. */
export const DEFAULT_HANDLE = 'out';
/** Handle opcional para onde o fluxo desvia quando um efeito falha. */
export const ERROR_HANDLE = 'error';
export const TRUE_HANDLE = 'true';
export const FALSE_HANDLE = 'false';
/** Variável preenchida pelo engine quando um efeito falha, útil em mensagens e condições. */
export const LAST_ERROR_VARIABLE = 'lastError';
const DEFAULT_MAX_STEPS = 200;
/**
 * Validação de resposta do visitante. Fica exportada aqui (e não escondida na UI) para o SDK
 * e o preview do painel recusarem exatamente as mesmas entradas.
 */
export function validateInput(inputType, value) {
    const text = (value ?? '').trim();
    if (!text)
        return { ok: false, error: 'Escreva uma resposta.' };
    switch (inputType) {
        case 'email':
            return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(text)
                ? { ok: true }
                : { ok: false, error: 'E-mail inválido.' };
        case 'phone': {
            const digits = text.replace(/\D/g, '');
            return digits.length >= 8 && digits.length <= 15
                ? { ok: true }
                : { ok: false, error: 'Telefone inválido. Inclua o DDD.' };
        }
        case 'number':
            return Number.isFinite(Number(text.replace(',', '.')))
                ? { ok: true }
                : { ok: false, error: 'Informe um número.' };
        case 'text':
        default:
            return { ok: true };
    }
}
function clampTimeout(value) {
    if (!value || !Number.isFinite(value))
        return DEFAULT_TIMEOUT_MS;
    return Math.min(Math.max(Math.trunc(value), 100), MAX_TIMEOUT_MS);
}
function isDefaultHandle(handle) {
    return handle === null || handle === undefined || handle === '' || handle === DEFAULT_HANDLE;
}
export function createEngine(config, options = {}) {
    const nodesById = new Map();
    for (const node of config.flow?.nodes ?? [])
        nodesById.set(node.id, node);
    const edges = config.flow?.edges ?? [];
    const maxSteps = options.maxStepsPerTurn ?? DEFAULT_MAX_STEPS;
    const initialVariables = { ...(options.initialVariables ?? {}) };
    let variables = { ...initialVariables };
    let transcript = [];
    let currentNodeId = null;
    let status = 'idle';
    let pending = null;
    function nextNodeId(nodeId, handle) {
        const candidates = edges.filter((edge) => edge.source === nodeId);
        if (handle === null) {
            const match = candidates.find((edge) => isDefaultHandle(edge.sourceHandle));
            return match?.target ?? null;
        }
        const match = candidates.find((edge) => edge.sourceHandle === handle);
        return match?.target ?? null;
    }
    function setVariable(name, value) {
        if (!name)
            return;
        variables = { ...variables, [name]: value };
    }
    function buildWebhookRequest(node) {
        const data = node.data;
        return {
            url: interpolate(data.url, variables),
            method: data.method,
            headers: interpolateDeep(data.headers ?? [], variables),
            contentType: data.contentType,
            ...(data.body !== undefined ? { body: interpolate(data.body, variables) } : {}),
            timeoutMs: clampTimeout(data.timeoutMs),
        };
    }
    /** Avança pelo grafo até precisar esperar o host, terminar ou dar erro. */
    function run() {
        const effects = [];
        let steps = 0;
        while (status === 'running') {
            if (steps > maxSteps) {
                status = 'error';
                const message = `Fluxo interrompido após ${maxSteps} passos — provável laço entre os blocos.`;
                transcript.push({ role: 'system', kind: 'error', text: message });
                effects.push({ kind: 'error', message, ...(currentNodeId ? { nodeId: currentNodeId } : {}) });
                break;
            }
            steps += 1;
            if (!currentNodeId) {
                status = 'ended';
                effects.push({ kind: 'end', reason: 'noOutgoingEdge' });
                break;
            }
            const node = nodesById.get(currentNodeId);
            if (!node) {
                status = 'ended';
                effects.push({ kind: 'end', reason: 'noOutgoingEdge' });
                break;
            }
            switch (node.type) {
                case 'message': {
                    const text = interpolate(node.data.text, variables);
                    transcript.push({ role: 'bot', kind: 'message', text, nodeId: node.id });
                    effects.push({ kind: 'message', nodeId: node.id, text });
                    currentNodeId = nextNodeId(node.id, null);
                    break;
                }
                case 'input': {
                    const prompt = interpolate(node.data.prompt, variables);
                    transcript.push({ role: 'bot', kind: 'prompt', text: prompt, nodeId: node.id });
                    const effect = {
                        kind: 'awaitInput',
                        nodeId: node.id,
                        prompt,
                        saveAs: node.data.saveAs,
                        inputType: node.data.inputType,
                        ...(node.data.placeholder ? { placeholder: node.data.placeholder } : {}),
                    };
                    pending = effect;
                    status = 'awaiting-input';
                    effects.push(effect);
                    break;
                }
                case 'buttons': {
                    const prompt = interpolate(node.data.prompt, variables);
                    transcript.push({ role: 'bot', kind: 'prompt', text: prompt, nodeId: node.id });
                    const effect = {
                        kind: 'awaitChoice',
                        nodeId: node.id,
                        prompt,
                        options: node.data.options.map((option) => ({
                            ...option,
                            label: interpolate(option.label, variables),
                        })),
                    };
                    pending = effect;
                    status = 'awaiting-choice';
                    effects.push(effect);
                    break;
                }
                case 'condition': {
                    let branch;
                    try {
                        branch = evaluateCondition(node.data, variables);
                    }
                    catch (error) {
                        const message = error instanceof ExpressionError
                            ? `Condição inválida: ${error.message}`
                            : `Falha ao avaliar condição: ${String(error)}`;
                        transcript.push({ role: 'system', kind: 'error', text: message, nodeId: node.id });
                        effects.push({ kind: 'error', nodeId: node.id, message });
                        setVariable(LAST_ERROR_VARIABLE, message);
                        currentNodeId = nextNodeId(node.id, FALSE_HANDLE) ?? nextNodeId(node.id, null);
                        break;
                    }
                    currentNodeId = nextNodeId(node.id, branch ? TRUE_HANDLE : FALSE_HANDLE);
                    break;
                }
                case 'browserCode': {
                    const effect = {
                        kind: 'runCode',
                        nodeId: node.id,
                        code: node.data.code ?? '',
                        executionMode: node.data.executionMode ?? 'sandbox',
                        ...(node.data.saveAs ? { saveAs: node.data.saveAs } : {}),
                        timeoutMs: clampTimeout(node.data.timeoutMs),
                    };
                    pending = effect;
                    status = 'awaiting-effect';
                    effects.push(effect);
                    break;
                }
                case 'browserWebhook': {
                    const effect = {
                        kind: 'browserWebhook',
                        nodeId: node.id,
                        request: buildWebhookRequest(node),
                        ...(node.data.saveAs ? { saveAs: node.data.saveAs } : {}),
                    };
                    pending = effect;
                    status = 'awaiting-effect';
                    effects.push(effect);
                    break;
                }
                case 'backendWebhook': {
                    // Só o nodeId sai daqui: a URL e os segredos vivem no servidor.
                    const effect = {
                        kind: 'backendWebhook',
                        nodeId: node.id,
                        ...(node.data.saveAs ? { saveAs: node.data.saveAs } : {}),
                    };
                    pending = effect;
                    status = 'awaiting-effect';
                    effects.push(effect);
                    break;
                }
                default: {
                    const unknown = node;
                    const message = `Tipo de bloco desconhecido: ${unknown.type}`;
                    status = 'error';
                    transcript.push({ role: 'system', kind: 'error', text: message, nodeId: unknown.id });
                    effects.push({ kind: 'error', nodeId: unknown.id, message });
                    break;
                }
            }
            if (status === 'running' && currentNodeId === null) {
                status = 'ended';
                effects.push({ kind: 'end', reason: 'flowEnd' });
                break;
            }
        }
        return effects;
    }
    function guard(expected, nodeId) {
        if (status !== expected) {
            return [
                {
                    kind: 'error',
                    message: `Estado inesperado: o fluxo está em "${status}".`,
                    ...(nodeId ? { nodeId } : {}),
                },
            ];
        }
        return null;
    }
    return {
        start() {
            variables = { ...initialVariables };
            transcript = [];
            pending = null;
            currentNodeId = config.flow?.startNodeId ?? null;
            if (!currentNodeId || !nodesById.has(currentNodeId)) {
                status = 'error';
                const message = 'Fluxo sem bloco inicial válido.';
                transcript.push({ role: 'system', kind: 'error', text: message });
                return [{ kind: 'error', message }];
            }
            status = 'running';
            return run();
        },
        submitInput(text) {
            const blocked = guard('awaiting-input');
            if (blocked)
                return blocked;
            const effect = pending;
            const validation = validateInput(effect.inputType, text);
            if (!validation.ok) {
                return [{ kind: 'error', nodeId: effect.nodeId, message: validation.error ?? 'Resposta inválida.' }];
            }
            const value = text.trim();
            setVariable(effect.saveAs, value);
            transcript.push({ role: 'visitor', kind: 'text', text: value, nodeId: effect.nodeId });
            pending = null;
            status = 'running';
            currentNodeId = nextNodeId(effect.nodeId, null);
            return run();
        },
        submitChoice(optionId) {
            const blocked = guard('awaiting-choice');
            if (blocked)
                return blocked;
            const effect = pending;
            const option = effect.options.find((o) => o.id === optionId);
            if (!option) {
                return [{ kind: 'error', nodeId: effect.nodeId, message: `Opção desconhecida: ${optionId}` }];
            }
            const node = nodesById.get(effect.nodeId);
            if (node?.type === 'buttons')
                setVariable(node.data.saveAs, option.value ?? option.label);
            transcript.push({
                role: 'visitor',
                kind: 'choice',
                text: option.label,
                optionId: option.id,
                nodeId: effect.nodeId,
            });
            pending = null;
            status = 'running';
            currentNodeId = nextNodeId(effect.nodeId, option.id) ?? nextNodeId(effect.nodeId, null);
            return run();
        },
        resolveEffect(nodeId, result) {
            // Resolução duplicada (ex.: resposta chegando junto com o timeout) não deve mexer no estado.
            if (status !== 'awaiting-effect' || !pending || pending.nodeId !== nodeId) {
                return [
                    {
                        kind: 'error',
                        nodeId,
                        message: `Resultado ignorado: o fluxo não está esperando o bloco ${nodeId}.`,
                    },
                ];
            }
            const effect = pending;
            pending = null;
            status = 'running';
            if (result.ok) {
                setVariable(effect.saveAs, result.value ?? null);
                currentNodeId = nextNodeId(nodeId, null);
                return run();
            }
            // Falhou: registra, avisa o host e desvia pelo handle de erro quando existir.
            const message = result.error || 'Falha ao executar o bloco.';
            setVariable(LAST_ERROR_VARIABLE, message);
            setVariable(effect.saveAs, null);
            transcript.push({ role: 'system', kind: 'error', text: message, nodeId });
            const errorBranch = nextNodeId(nodeId, ERROR_HANDLE);
            currentNodeId = errorBranch ?? nextNodeId(nodeId, null);
            return [{ kind: 'error', nodeId, message }, ...run()];
        },
        getState() {
            return { status, currentNodeId, variables: { ...variables }, pending };
        },
        getVariables() {
            return { ...variables };
        },
        getTranscript() {
            return [...transcript];
        },
        getPending() {
            return pending;
        },
        reset() {
            variables = { ...initialVariables };
            transcript = [];
            currentNodeId = null;
            pending = null;
            status = 'idle';
        },
    };
}
export { stringifyValue };
//# sourceMappingURL=engine.js.map